import {Component, computed, input} from '@angular/core';
import {TableModule} from 'primeng/table';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  ProgressDashboardBoxComponent,
  ProgressDashboardBoxType,
} from './progress-dashboard-box/progress-dashboard-box.component';
import {
  AttributesByEvaluationCriteriaMap,
  SubCriteriaAttributeItem,
} from '../subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';
import {EvaluationCriteria, EvaluationSubCriteriaDirectus} from '@frontend/directus';
import {ProgressDashboardPieChartComponent} from './progress-dashboard-pie-chart/progress-dashboard-pie-chart.component';
import {ProgressDashboardRfiTableComponent} from './progress-dashboard-table/progress-dashboard-rfi-table/progress-dashboard-rfi-table.component';
import {ProgressDashboardRfpTableComponent} from './progress-dashboard-table/progress-dashboard-rfp-table/progress-dashboard-rfp-table.component';
import {ProgressDashboardRfiBarChartComponent} from './progress-dashboard-bar-chart/progress-dashboard-rfi-bar-chart/progress-dashboard-rfi-bar-chart.component';
import {ProgressDashboardRfpBarChartComponent} from './progress-dashboard-bar-chart/progress-dashboard-rfp-bar-chart/progress-dashboard-rfp-bar-chart.component';

@Component({
  selector: 'app-progress-dashboard',
  imports: [
    TableModule,
    Accordion,
    AccordionPanel,
    TranslocoPipe,
    ProgressDashboardBoxComponent,
    AccordionHeader,
    AccordionContent,
    ProgressDashboardPieChartComponent,
    ProgressDashboardRfiBarChartComponent,
    ProgressDashboardRfpBarChartComponent,
    ProgressDashboardRfiTableComponent,
    ProgressDashboardRfpTableComponent,
  ],
  templateUrl: './progress-dashboard.component.html',
})
export class ProgressDashboardComponent {
  readonly attributesByEvaluationCriteria = input.required<AttributesByEvaluationCriteriaMap>({});
  readonly evaluationCriteria = input.required<EvaluationCriteria[]>();
  readonly evaluationSubCriteria = input<EvaluationSubCriteriaDirectus[]>([]);
  readonly phase = input.required<string>();
  readonly pieChartLabel = input<string>();

  readonly totalAttributes = computed(() => {
    const ecIds = Object.keys(this.attributesByEvaluationCriteria() || {});
    const subCriteriaIds = ecIds.flatMap((ecId: string) =>
      Object.keys(this.attributesByEvaluationCriteria()[ecId as unknown as number] || {}),
    );

    const allAttributes = ecIds.flatMap((ecId) => {
      return (
        subCriteriaIds.flatMap((subCriteriaId) => {
          return this.attributesByEvaluationCriteria()[ecId as unknown as number][
            subCriteriaId as unknown as number
          ];
        }) || []
      ).filter((attribute) => attribute !== undefined);
    });

    return new Set(allAttributes)?.size || 0;
  });

  readonly completedSubCriteria = computed<number>(() => {
    const attributesByCriteria = this.attributesByEvaluationCriteria() ?? {};

    const completed = Object.values(attributesByCriteria).flatMap((subCriteriaMap) =>
      Object.values<SubCriteriaAttributeItem[]>(subCriteriaMap).flatMap((attributes) =>
        attributes.filter((attribute) => {
          // Prefer generic completion flag if present:
          if (attribute?.isCompleted !== undefined) return Boolean(attribute.isCompleted);

          // Backward-compatible fallback (existing behavior)
          return attribute?.selectedImportanceScore !== undefined;
        }),
      ),
    );

    return new Set(completed).size;
  });

  readonly numCriteria = computed(
    () => new Set(Object.keys(this.attributesByEvaluationCriteria() || {})).size,
  );

  readonly numSubCriteria = computed(() => {
    const ecIds = Object.keys(this.attributesByEvaluationCriteria() || {});
    const subCriteriaIds = ecIds.flatMap((ecId: string) =>
      Object.keys(this.attributesByEvaluationCriteria()[ecId as unknown as number] || {}),
    );
    return new Set(subCriteriaIds)?.size || 0;
  });

  readonly pctCompleted = computed(() => {
    const completed = this.completedSubCriteria();
    return this.totalAttributes() > 0 ? Math.floor((completed / this.totalAttributes()) * 100) : 0;
  });

  readonly flattenedAttributesByEvaluationCriteriaMap = computed<SubCriteriaAttributeItem[]>(() =>
    this.flattenAttributesByEvaluationCriteriaMap(this.attributesByEvaluationCriteria()),
  );

  protected flattenAttributesByEvaluationCriteriaMap(
    attributesByCriteria: AttributesByEvaluationCriteriaMap,
  ): SubCriteriaAttributeItem[] {
    const allAttributes: SubCriteriaAttributeItem[] = [];

    Object.values(attributesByCriteria).forEach((subCriteriaMap) => {
      Object.values<SubCriteriaAttributeItem[]>(subCriteriaMap).forEach((attributes) => {
        allAttributes.push(...attributes);
      });
    });

    return allAttributes;
  }

  protected readonly ProgressDashboardBoxType = ProgressDashboardBoxType;
}
